// ---------------------------------------------------------------------------
// CV.4: Cookie Consent Banner — GDPR compliant
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback } from "react";
import { Shield, Settings, X } from "lucide-react";
import {
  getStoredConsentPreferences,
  persistConsentPreferences,
  type ConsentPreferences,
  upgradeLegacyConsentStorage,
} from "@/lib/cookie-consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = upgradeLegacyConsentStorage() ?? getStoredConsentPreferences();
    if (!consent) {
      setVisible(true);
      return;
    }

    setAnalytics(consent.analytics);
    setMarketing(consent.marketing);
  }, []);

  const acceptAll = useCallback(() => {
    const prefs: ConsentPreferences = { essential: true, analytics: true, marketing: true, consentedAt: new Date().toISOString() };
    persistConsentPreferences(prefs);
    setVisible(false);
  }, []);

  const rejectAll = useCallback(() => {
    const prefs: ConsentPreferences = { essential: true, analytics: false, marketing: false, consentedAt: new Date().toISOString() };
    persistConsentPreferences(prefs);
    setVisible(false);
  }, []);

  const savePreferences = useCallback(() => {
    const prefs: ConsentPreferences = { essential: true, analytics, marketing, consentedAt: new Date().toISOString() };
    persistConsentPreferences(prefs);
    setVisible(false);
    setShowPreferences(false);
  }, [analytics, marketing]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
      <div className="mx-auto max-w-3xl rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-xl">
        {showPreferences ? (
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Cookie Preferences</h3>
              <button onClick={() => setShowPreferences(false)} className="rounded-md p-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-[var(--color-border)] p-3">
                <div>
                  <p className="text-sm font-medium text-[var(--color-foreground)]">Essential</p>
                  <p className="text-xs text-[var(--color-muted-foreground)]">Required for the platform to function. Cannot be disabled.</p>
                </div>
                <div className="rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-success)]">Always on</div>
              </div>

              <label className="flex items-center justify-between rounded-lg border border-[var(--color-border)] p-3 cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-[var(--color-foreground)]">Analytics</p>
                  <p className="text-xs text-[var(--color-muted-foreground)]">Helps us understand how you use the platform to improve it.</p>
                </div>
                <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="h-4 w-4 rounded border-[var(--color-border)]" />
              </label>

              <label className="flex items-center justify-between rounded-lg border border-[var(--color-border)] p-3 cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-[var(--color-foreground)]">Marketing</p>
                  <p className="text-xs text-[var(--color-muted-foreground)]">Used to show relevant content and measure campaign effectiveness.</p>
                </div>
                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="h-4 w-4 rounded border-[var(--color-border)]" />
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={rejectAll} className="rounded-lg px-4 py-2 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                Reject All
              </button>
              <button onClick={savePreferences} className="btn-press rounded-lg px-4 py-2 text-sm font-medium text-white" style={{ background: "var(--gradient-energy)" }}>
                Save Preferences
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4 p-4">
            <Shield className="h-5 w-5 text-[var(--color-primary)] flex-shrink-0" />
            <p className="flex-1 text-sm text-[var(--color-foreground)]">
              We use cookies to improve your experience.{" "}
              <a href="/cookies" target="_blank" rel="noopener noreferrer" className="underline text-[var(--color-primary)]">Cookie Policy</a>.
              {" "}Manage your preferences anytime.
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => {
                  const current = getStoredConsentPreferences();
                  setAnalytics(current?.analytics ?? false);
                  setMarketing(current?.marketing ?? false);
                  setShowPreferences(true);
                }}
                className="rounded-lg px-3 py-1.5 text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] flex items-center gap-1"
              >
                <Settings className="h-3 w-3" /> Manage
              </button>
              <button onClick={rejectAll} className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-foreground)] hover:bg-[var(--color-muted)]">
                Reject
              </button>
              <button onClick={acceptAll} className="btn-press rounded-lg px-4 py-1.5 text-xs font-medium text-white" style={{ background: "var(--gradient-energy)" }}>
                Accept All
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
